import React, { useState, useRef, useEffect } from 'react';
import { styled, alpha } from '@mui/material/styles';
import InputBase from '@mui/material/InputBase';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import { Box, Paper, Typography, Divider, List, ListItem, ListItemText, ListItemButton } from '@mui/material';

import { apiFetch } from '../../../utils/api';

const Search = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.white, 0.15),
  '&:hover': {
    backgroundColor: alpha(theme.palette.common.white, 0.25),
  },
  marginLeft: 0,
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    marginLeft: theme.spacing(1),
    width: 'auto',
  },
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#FFFFFF'
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: '#FFFFFF',
  width: '100%',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create('width'),
    [theme.breakpoints.up('sm')]: {
      width: '20ch',
      '&:focus': {
        width: '28ch',
      },
    },
  },
}));

const SearchComponent = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState({ recipes: [], tags: [] });
  const [showResults, setShowResults] = useState(false);
  const navigate = useNavigate();
  const searchRef = useRef(null);

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      navigate(`/search?query=${encodeURIComponent(searchQuery)}`);
      setShowResults(false);
    }
  };

  const handleSearchChange = async (event) => {
    const query = event.target.value;
    setSearchQuery(query);

    if (query.length > 0) {
      const response = await apiFetch(`/api/search?query=${encodeURIComponent(query)}`);
      const data = await response.json();
      setSearchResults(data);
      setShowResults(true);
    } else {
      setShowResults(false);
    }
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    if (searchQuery) {
      navigate(`/search?query=${encodeURIComponent(searchQuery)}`);
      setShowResults(false);
    }
  };

  const handleItemClick = (path) => {
    setShowResults(false);
    navigate(path);
  };

  const handleClickOutside = (event) => {
    if (searchRef.current && !searchRef.current.contains(event.target)) {
      setShowResults(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <Box sx={{ position: 'relative', width: '100%' }} ref={searchRef}>
      <Search onSubmit={handleSearchSubmit} component="form">
        <SearchIconWrapper>
          <SearchIcon />
        </SearchIconWrapper>
        <StyledInputBase
          placeholder="Buscar..."
          inputProps={{ 'aria-label': 'search' }}
          value={searchQuery}
          onChange={handleSearchChange}
          onKeyPress={handleKeyPress}
        />
      </Search>
      {showResults && (
        <Paper sx={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1000, maxHeight: 300, overflowY: 'auto' }}>
          <List dense>
            <Typography variant="subtitle1" sx={{ padding: 1, backgroundColor: '#f7f7f7' }}>Recetas</Typography>
            {searchResults.recipes.slice(0, 5).map((recipe) => (
              <ListItem key={recipe._id} disablePadding>
                <ListItemButton onClick={() => handleItemClick(`/recipes/${recipe._id}`)}>
                  <ListItemText primary={recipe.title} />
                </ListItemButton>
              </ListItem>
            ))}
            {searchResults.recipes.length > 5 && (
              <ListItem disablePadding>
                <ListItemButton onClick={() => handleItemClick(`/search?query=${encodeURIComponent(searchQuery)}&type=recipes`)}>
                  <ListItemText primary="Más..." />
                </ListItemButton>
              </ListItem>
            )}
            <Divider />
            <Typography variant="subtitle1" sx={{ padding: 1, backgroundColor: '#f7f7f7' }}>Etiquetas</Typography>
            {searchResults.tags.slice(0, 5).map((tag) => (
              <ListItem key={tag._id} disablePadding>
                <ListItemButton onClick={() => handleItemClick(`/search?query=${encodeURIComponent(tag.name)}&type=tags`)}>
                  <ListItemText primary={tag.name} />
                </ListItemButton>
              </ListItem>
            ))}
            {searchResults.tags.length > 5 && (
              <ListItem disablePadding>
                <ListItemButton onClick={() => handleItemClick(`/search?query=${encodeURIComponent(searchQuery)}&type=tags`)}>
                  <ListItemText primary="Más..." />
                </ListItemButton>
              </ListItem>
            )}
          </List>
        </Paper>
      )}
    </Box>
  );
};

export default SearchComponent;
